import { useContext, useMemo } from 'react';

import { DevContext } from './../../../ricos-context/DevContext';
import { type RequestService } from './../../request-service-provider/request-service-provider';
import { useRequestService } from './../../request-service-provider/request-service-provider';

import type { ItemsResponseBody } from '../serverless-types/serverless-types';
import type {
  FetchSitePages,
  FetchSitePagesParams,
  SitePagesItem,
  SitePagesPageType,
} from '../types';

const pageTypePaths: Record<SitePagesPageType, string> = {
  blogPost: 'blog-posts',
  staticPage: 'static-pages',
};

export const useFetchSitePages = (): FetchSitePages => {
  const { makeRequest } = useRequestService();
  const fetchSitePages = useMemo<FetchSitePages>(
    () => (pageType, params) =>
      (async (
        makeRequest: RequestService['makeRequest'],
        pageType: SitePagesPageType,
        params: FetchSitePagesParams
      ): Promise<ItemsResponseBody> => {
        const response = await makeRequest(`/_api/ricos-site-pages/${pageTypePaths[pageType]}`, {
          body: JSON.stringify(params),
          headers: {
            'Content-Type': 'application/json',
          },
          method: 'POST',
        });

        const result = await response.json();
        return {
          ...result,
          items:
            result.items.map((item: SitePagesItem) => ({
              ...item,
              updatedDate: item.updatedDate ? new Date(item.updatedDate) : undefined,
            })) || [],
        };
      })(makeRequest, pageType, params),
    [makeRequest]
  );

  return useContext(DevContext)?.fetchSitePages || fetchSitePages;
};
